<template>
    <div>
        <div class="border-bottom pb-1">
            <div class="d-flex justify-content-between align-items-end">
                <div v-if="modal"></div>
                <h5 v-else class="font-weight-bold">Document Notes</h5>
                <div>
                    <button v-show="showInput" type="button" class="btn btn-sm btn-primary mr-1"  title="Add Note" @click="save">
                        <i class="fas fa-save fa-lg fa-fw"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-primary"  title="Add Note" @click="toggle">
                        <i class="fas fa-comment fa-lg fa-fw"></i>
                    </button>
                </div>
            </div>
            <div v-show="showInput" class="my-2">
                <textarea id="note" name="note" class="form-control mb-2" rows="8" v-model="text" ref="note" autofocus></textarea>
                <div class="form-check ml-2">
                    <input id="privacy" class="form-check-input" type="checkbox" v-model="privacy">
                    <label class="form-check-label" for="privacy">
                        <i class="fas fa-lock fa-md text-muted"></i>
                        <span class="nudge-down">Make this note private visible only to me.</span>
                    </label>
                </div>                
            </div>
        </div>
        <div v-if="documentNotes.length">
            <div v-for="note in documentNotes" :key="'document-note-' + note.id">
                <div v-if="confirmId === note.id" class="d-flex flex-row justify-content-end mt-1">
                    <div class="pr-3">
                        <span class="font-weight-bold mr-2">Are you sure you want to delete this note?</span>
                        <button class="btn btn-sm btn-danger" @click="deleteNote(note.id)">Delete</button>
                        <button class="btn btn-sm btn-secondary" @click="confirmId = null">Cancel</button>
                    </div>
                </div>                
                <div class="d-flex flex-row mt-3">
                    <div class="px-3 flex-grow-1" v-text="note.text"></div>
                    <div v-if="note.can_edit" class="d-flex mb-auto">
                        <button class="btn btn-link btn-lg p-0 mx-1" @click="edit(note)" title="Edit Note">
                            <i class="fas fa-edit edit-button"></i>
                        </button>
                        <button class="btn btn-link btn-lg p-0 mx-1" @click="togglePrivacy(note)" title="Toggle Privacy">
                            <i :class="['fas edit-button', note.private ? 'fa-lock' : 'fa-lock-open']"></i>
                        </button>                                    
                        <button class="btn btn-link btn-lg p-0 mx-1" @click="confirmId = note.id" title="Delete Note">
                            <i class="fas fa-trash-alt edit-button"></i>
                        </button>
                    </div>
                </div>
                <div class="d-flex flex-row justify-content-end border-bottom mt-1">
                    <div class="text-muted pb-2 pr-3">
                        <small>{{ note.creator + ' - ' }}{{ note.created_at | localizeTime('lll') }}</small>
                        <i v-if="note.created_at != note.updated_at" class="fas fa-pencil-alt fa-sm text-muted" :title="getEditedString(note.updated_at)"></i>
                    </div>
                </div>               
            </div>
        </div>
        <div v-else-if="loading" class="mt-3">            
            <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
        </div>
        <div v-else class="mt-3">
            <h4 class="text-muted text-center">No Document Notes</h4>
        </div>
    </div>  
</template>

<script>
import { DocumentType } from '../../constants.js';

export default {
    props: {
        dataClientId: Number,
        dataClaimId: Number,
        dataDocumentId: Number,
        dataDocumentTypeId: Number,
        dataHistorical: Boolean,
        modal: Boolean
    },
    data() {
        return {
            clientId: this.dataClientId,
            claimId: this.dataClaimId,
            documentId: this.dataDocumentId,
            documentTypeId: this.dataDocumentTypeId,
            historical: this.dataHistorical,
            loading: false,
            documentNotes: [],
            noteId: null,
            text: null,
            privacy: false,
            showInput: false,
            confirmId: null,

            DocumentType: DocumentType
        }
    },
    computed: {
        notesChannel: function () {
            let retval = `document-notes-event.${this.documentId}.${this.clientId}`;
            if (this.historical) {
                
                if ([DocumentType.CMS_1500, DocumentType.CMS_1450, DocumentType.CMS_FORM].includes(this.documentTypeId)) {
                    retval = `historical-document-notes-event.service.${this.documentId}.${this.clientId}`;
                }
                else {
                    retval = `historical-document-notes-event.document.${this.documentId}.${this.clientId}`;
                }
            }

            return retval;
        },     
        href: function () {
            let retval = `/ajax/claims/${this.claimId}/documents/${this.documentId}/notes`;
            if (this.historical) {

                if ([DocumentType.CMS_1500, DocumentType.CMS_1450, DocumentType.CMS_FORM].includes(this.documentTypeId)) {
                    retval = `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/services/${this.documentId}/notes`;
                }
                else {
                    retval = `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${this.documentId}/notes`;
                }              
            }

            return retval;
        }
    },
    methods: {
        refresh: function () {

            this.loading = true;

            axios.get(this.href)
                .then(response => {
                    this.documentNotes = response.data.notes;
                    this.loading = false;       
                })
                .catch(error => { 
                    this.loading = false;

                    if (error.response.data.message) {
                        let message = error.response.data.message;
                        toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                    }                    
                });
        },
        reset: function () {
            this.noteId = null;
            this.text = null;
            this.privacy = false;
        },
        focus: function () {
            let self = this;
            this.$nextTick(() => {
                self.$refs.note.focus();                    
            });
        },         
        toggle: function () {
            this.showInput = !this.showInput;
            this.reset();
            this.focus();           
        },
        edit: function (note) {

            if (note.can_edit) {

                this.noteId = note.id;
                this.text = note.text;
                this.privacy = note.private;
                this.showInput = true;

                this.focus();
            }
        },
        save: function() {

            if (this.noteId) {

                // if we're editing a note
                axios.patch(`${this.href}/${this.noteId}`, {
                    text: this.text,
                    private: this.privacy
                })
                .catch(error => {
                    if (error.response.data.message) {
                        let message = error.response.data.message;
                        toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                    }  
                });
            }            
            else {

                // else this is a new note
                axios.post(this.href, {
                    text: this.text,
                    private: this.privacy
                })          
                .catch(error => {
                    if (error.response.data.message) {
                        let message = error.response.data.message;
                        toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                    } 
                });                
            }
            
            this.reset();
            this.showInput = false;
        },
        getEditedString: function (value) {

            const editDateTime = this.$options.filters.localizeTime(value, 'lll');
            return 'Edited ' + editDateTime;
        },
        deleteNote: function (id) {

            axios.delete(`${this.href}/${id}`)
                .catch(error => {
                    if (error.response.data.message) {
                        let message = error.response.data.message;
                        toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                    }                     
                });
            this.confirm = false;
        },
        togglePrivacy: function (note) {

            let self = this;
            let privacy = !note.private;

            axios.patch(`${this.href}/${note.id}/privacy`, {
                private: privacy
            })
            .catch(error => {
                if (error.response.data.message) {
                    let message = error.response.data.message;
                    toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                }            
            });
        },
        init: function (clientId, claimId, documentId, documentTypeId, historical) {

            this.clientId = clientId;
            this.claimId = claimId;
            this.documentId = documentId;
            this.documentTypeId = documentTypeId;
            this.historical = historical;
            this.showInput = false;

            // listen for document note events
            window.Echo.private(this.notesChannel)
                .listen('DocumentNoteEvent', (e) => {
                    this.refresh();
                });
                
            this.refresh();
        }                                                        
    },
    created() {

        if (!this.modal) {

            this.refresh();
            
            // listen for document note events
            window.Echo.private(this.notesChannel)
                .listen('DocumentNoteEvent', (e) => {
                    this.refresh();
                });
        }
    }
}
</script>

<style scoped>
    .edit-button {
        font-size: 1em;
    }

    .nudge-down {
        position: relative;
        top: 2px;
    }
</style>